import type { RowValue } from "../../../lib/csv";
import { uniq } from "diagnostics-lib/array";
import { normalize } from "diagnostics-lib/string";
export interface MapFile<T, U extends T> {
  fileName: string[];
  toCreate: {
    column: U;
    values: string[];
  }[];
  error: {
    format: {
      message: string;
      data: RowValue[] | T[];
      rowNames?: string[];
      columnNames?: string[];
      lines?: string[];
    }[];
    permissionRequired: {
      message: string;
      column: U;
      data: RowValue[] | T[];
      rowNames?: string[];
      columnNames?: string[];
      lines?: string[];
    }[];
  };
}

//? Common quantities and failures
export type Options<T extends string> = Record<
  T,
  {
    format?: { validator: (content: RowValue[]) => RowValue[]; message: string }[];
    permission?: {
      validator: (content: RowValue[], data: string[]) => RowValue[];
      message: string;
      multiple?: boolean;
    }[];
  }
>;

//? Quantities and Failures
export function createCSVValidator<T extends string, U extends Extract<T, string>>(
  validation: MapFile<Partial<T>, U>,
  content: RowValue[],
  options: Options<T>,
  permissionInfos: {
    normalizedPresentData: Record<U, string[]>;
    canEdit: Record<U, boolean>;
  },
) {
  for (const key in options) {
    const { format = [], permission = [] } = options[key];
    for (const { validator, message } of format) {
      if (!validator || !message) throw new Error("Missing validator or message");
      const validatorResult = validator(content);
      if (validatorResult.length) {
        const error = {
          message,
          data: validatorResult,
          rowNames: validatorResult.map(m => m["technicalName"] as string),
          lines: validatorResult.map(m => m["line"] as string),
        };
        validation.error.format.push(error);
      }
    }
    for (const { validator, message, multiple } of permission) {
      if (!validator || !message) throw new Error("Missing validator or message");
      const normalizedPresentData = permissionInfos.normalizedPresentData[key as U];
      if (normalizedPresentData) {
        const validatorResult = validator(content, normalizedPresentData);
        if (validatorResult.length) {
          const editable = permissionInfos.canEdit[key as U];
          if (editable) {
            const values = validatorResult.flatMap(m =>
              multiple ? m[key]?.split(",") || [] : m[key],
            );
            const nonPresents = values.flatMap(elem => {
              if (elem && !normalizedPresentData?.includes(normalize(elem))) {
                return [elem];
              }
              return [];
            });

            validation.toCreate.push({
              column: key as U,
              values: uniq(nonPresents).sort(),
            });
          } else {
            validation.error.permissionRequired.push({
              column: key as U,
              message,
              data: validatorResult,
            });
          }
        }
      }
    }
  }
}

interface ResponseWithBody<T> {
  body: T;
}

interface ErrorResponse {
  status: number;
  message: string;
}

export const createAndRefreshItems = async <T extends string, V>(
  toCreate: T[],
  createFunc: (name: T) => Promise<ResponseWithBody<V>>,
  newItems: V[],
) => {
  for (const item of toCreate) {
    try {
      const res = await createFunc(item);
      newItems.push(res.body);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: ErrorResponse | any) {
      if (e && e.status === 409) {
        //? Already exist
        continue;
      } else {
        throw e;
      }
    }
  }

  return newItems;
};

export const validatorDataNotPresent = (content: RowValue[], data: string[], column: string) =>
  content.filter(e => {
    const item = e[column];
    return item && !data.includes(normalize(item));
  });
export const validatorMultipleDataNotPresent = (
  content: RowValue[],
  data: string[],
  column: string,
) =>
  content.filter(e => {
    const items = e[column]?.split(",").map(e => e.trim());
    return items?.some(items => !data.includes(normalize(items)));
  });
export const validatorWrongFormatData = (content: RowValue[], column: string) =>
  content.filter(e => {
    const items = e[column];
    if (!items) return false;
    const itemsRegexp = /^[^,].*[^,]$/;
    const check = itemsRegexp.test(items);
    if (!check) return true;
    return false;
  });
